import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '@/components/Select';
import { getPlayableLinks, getPlayableLinkStats } from '@/api/playables';

function fmtCents(c: number) {
  return c ? `$${(c / 100).toFixed(2)}` : '—';
}
function pct(num: number, denom: number) {
  if (!denom) return '—';
  return `${(num / denom * 100).toFixed(2)}%`;
}

export function PlayableStats() {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(weekAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [linkId, setLinkId] = useState('');

  const { data: links = [] } = useQuery({ queryKey: ['playable-links'], queryFn: getPlayableLinks });

  const linkOptions = useMemo(() => [
    { value: '', label: '— select link —' },
    ...links.map((l) => ({ value: l.id, label: `${l.name}  (${l.domain_hostname}/p/${l.slug})` })),
  ], [links]);

  const { data, isLoading } = useQuery({
    queryKey: ['playable-link-stats', linkId, from, to],
    queryFn: () => getPlayableLinkStats(linkId, {
      from: from ? `${from} 00:00:00` : undefined,
      to:   to   ? `${to} 23:59:59`   : undefined,
    }),
    enabled: !!linkId,
  });

  const rows = data?.rows || [];
  const totals = data?.totals;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Playable Stats</h1>
        <p className="text-sm text-content-subtle mt-1">Per-item performance: hits → flow clicks → regs/deps</p>
      </div>

      <div className="bg-surface border border-border rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[20rem] flex-1">
          <label className="block text-xs text-content-subtle mb-1">Link</label>
          <Select value={linkId} options={linkOptions} onChange={setLinkId} searchable />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-3 py-2 border border-border rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs text-content-subtle mb-1">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-3 py-2 border border-border rounded-lg text-sm" />
        </div>
      </div>

      {!linkId && (
        <div className="bg-surface border border-border rounded-lg p-8 text-center text-content-subtle text-sm">
          Pick a playable link above to see stats.
        </div>
      )}

      {linkId && (
        <>
          {totals && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
              <Total label="Hits" value={totals.hits.toString()} />
              <Total label="Unique" value={totals.unique_hits.toString()} />
              <Total label="Flow clicks" value={totals.flow_clicks.toString()} />
              <Total label="Regs" value={`${totals.regs} (${pct(totals.regs, totals.flow_clicks)})`} />
              <Total label="Deps" value={`${totals.deps} (${pct(totals.deps, totals.flow_clicks)})`} />
              <Total label="Payout" value={fmtCents(totals.payout_cents)} />
            </div>
          )}

          <div className="bg-surface border border-border rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-muted text-xs text-content-subtle uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">Playable</th>
                  <th className="px-4 py-2 text-left">Campaign</th>
                  <th className="px-4 py-2 text-right">Weight</th>
                  <th className="px-4 py-2 text-right">Hits</th>
                  <th className="px-4 py-2 text-right">Unique</th>
                  <th className="px-4 py-2 text-right">CTR (clicks/hits)</th>
                  <th className="px-4 py-2 text-right">Flow clicks</th>
                  <th className="px-4 py-2 text-right">Regs (CR)</th>
                  <th className="px-4 py-2 text-right">Deps (CR)</th>
                  <th className="px-4 py-2 text-right">Payout</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                {isLoading && <tr><td colSpan={10} className="text-center py-8 text-content-subtle">Loading…</td></tr>}
                {!isLoading && rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-content-subtle">No data for this range</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} className={r.status !== 'active' ? 'opacity-60' : ''}>
                    <td className="px-4 py-2">{r.playable_name || r.playable_id}</td>
                    <td className="px-4 py-2 text-content-muted text-xs">
                      {r.campaign_name || r.target_campaign_id}
                      {r.campaign_slug && <span className="block text-[11px] font-mono text-content-subtle">/{r.campaign_slug}</span>}
                    </td>
                    <td className="px-4 py-2 text-right">{r.weight}</td>
                    <td className="px-4 py-2 text-right">{r.hits}</td>
                    <td className="px-4 py-2 text-right">{r.unique_hits}</td>
                    <td className="px-4 py-2 text-right text-xs text-content-muted">{pct(r.flow_clicks, r.hits)}</td>
                    <td className="px-4 py-2 text-right">{r.flow_clicks}</td>
                    <td className="px-4 py-2 text-right">{r.regs} <span className="text-xs text-content-subtle">({pct(r.regs, r.flow_clicks)})</span></td>
                    <td className="px-4 py-2 text-right">{r.deps} <span className="text-xs text-content-subtle">({pct(r.deps, r.flow_clicks)})</span></td>
                    <td className="px-4 py-2 text-right">{fmtCents(r.payout_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-xs text-content-subtle">
            Conversions are attributed by playable_id (not by item_id) — items reusing the same playable share its conversion totals.
          </p>
        </>
      )}
    </div>
  );
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface border border-border rounded-lg p-3">
      <div className="text-xs text-content-subtle">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
